/**
 * 帳票「日次決算書」の判断（F-13・`docs/02` §7.2）。★計算はしない。サーバの値を文字に写すだけ。
 *
 * ★`null` は**空欄**（表のセルは空文字）。0 にも「-」にもしない。月次の要約だけは「未確定」と言う
 *   （空欄だと、印刷した紙で「0 だった」のか「出せなかった」のか分からないため）。
 * ★載せる列の根拠は `docs/02` §7.2。**列を足すときは先にそちらへ。**
 */
import type { DailyStatement, DailyStatementRow } from "./api.js";
import { IMPORTED_PURCHASES_WARNING, MONTH_PENDING, MONTH_PENDING_NOTE } from "./profit-loss-view.js";

const WEEKDAY = ["日", "月", "火", "水", "木", "金", "土"] as const;

const num = (n: number | null): string => (n === null ? "" : Math.round(n).toLocaleString());
const one = (n: number | null): string => (n === null ? "" : n.toFixed(1));
const pct = (r: number | null): string => (r === null ? "" : `${(r * 100).toFixed(1)}%`);
const yen = (n: number): string => `${n.toLocaleString()}円`;
const md = (d: string): string => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;

/** 列の見出し（2段）。`group` が同じ列は上の段で束ねる */
export const STATEMENT_COLUMNS: readonly { group: string; label: string }[] = [
  { group: "", label: "日" },
  { group: "", label: "曜日" },
  { group: "売上（税抜）", label: "目標" },
  { group: "売上（税抜）", label: "目標累計" },
  { group: "売上（税抜）", label: "実績" },
  { group: "売上（税抜）", label: "累計" },
  { group: "売上（税抜）", label: "累計差額" },
  { group: "売上（税抜）", label: "達成率" },
  { group: "原価率（概算）", label: "当日" },
  { group: "原価率（概算）", label: "累計" },
  { group: "粗利（概算）", label: "当日" },
  { group: "粗利（概算）", label: "累計" },
  { group: "人件費率", label: "当日" },
  { group: "人件費率", label: "累計" },
  { group: "FL", label: "累計" },
  { group: "客数", label: "目標" },
  { group: "客数", label: "実数" },
  { group: "客数", label: "差額" },
  { group: "", label: "客単価" },
  { group: "", label: "仕入" },
  { group: "固定費", label: "当日" },
  { group: "固定費", label: "累計" },
  { group: "営業利益（概算）", label: "当日" },
  { group: "営業利益（概算）", label: "累計" },
  { group: "目標差額", label: "目標利益" },
  { group: "目標差額", label: "利益達成" },
];

/** 1日ぶんの行。★`STATEMENT_COLUMNS` と同じ並び・同じ数 */
export function statementRowCells(r: DailyStatementRow): string[] {
  return [
    String(Number(r.date.slice(8, 10))),
    `${WEEKDAY[r.weekday] ?? ""}${r.isOpen === false ? "（休）" : ""}`,
    num(r.salesTarget),
    num(r.salesTargetCumulative),
    num(r.sales),
    num(r.salesCumulative),
    num(r.salesVarianceCumulative),
    pct(r.salesAchievementCumulative),
    pct(r.costRate),
    pct(r.costRateCumulative),
    num(r.grossProfit),
    num(r.grossProfitCumulative),
    pct(r.laborCostRate),
    pct(r.laborCostRateCumulative),
    pct(r.flRateCumulative),
    one(r.guestsTarget),
    num(r.guests),
    one(r.guestsVariance),
    num(r.perGuest),
    num(r.purchases),
    num(r.fixedCosts),
    num(r.fixedCostsCumulative),
    num(r.operatingProfit),
    num(r.operatingProfitCumulative),
    num(r.profitTarget),
    pct(r.profitAchievementCumulative),
  ];
}

/** 締め状態の言い方。★`CLOSED`（確定）以外は、紙で見ても分かるように「暫定（未締め）」 */
export const STATEMENT_PROVISIONAL = "暫定（未締め）";
const closingLabel = (s: DailyStatement): string => (s.closingStatus === "CLOSED" ? "締め済み" : STATEMENT_PROVISIONAL);

/** `2026-10-07T13:40:05` → `2026-10-07 13:40`（日本時間） */
const stamp = (t: string): string => t.slice(0, 16).replace("T", " ");

/** その月に売上の入力が1日も無い（定休日の 0 は入力と数えない）ときの一文。あれば表の上に出す */
export function statementEmptyNote(s: DailyStatement): string | null {
  const none = s.rows.every((r) => r.sales === null || (r.isOpen === false && r.sales === 0));
  return none ? "この月は売上の入力がありません" : null;
}

/** 見出し部（年月・店舗・出力日時・締め状態・営業日数・月間目標） */
export function statementHeaderLines(s: DailyStatement): { label: string; value: string }[] {
  return [
    { label: "年月", value: `${s.yearMonth.slice(0, 4)}年${Number(s.yearMonth.slice(5, 7))}月` },
    { label: "店舗", value: s.shopName },
    { label: "出力日時", value: stamp(s.generatedAt) },
    { label: "状態", value: closingLabel(s) },
    { label: "営業日数", value: s.businessDays === null ? "未確定" : `${String(s.businessDays)}日` },
    { label: "月間売上目標（税抜）", value: s.monthTargetSales === null ? "未設定" : yen(s.monthTargetSales) },
    { label: "月間客数目標", value: s.monthTargetGuests === null ? "未設定" : `${s.monthTargetGuests.toLocaleString()}人` },
    { label: "利益目標", value: s.monthTargetProfit === null ? "未設定" : yen(s.monthTargetProfit) },
    // ★派生値は、元の目標が揃わなければ「未設定」（0 にしない）
    { label: "目標客単価", value: s.targetPerGuest === null ? "未設定" : yen(Math.round(s.targetPerGuest)) },
    { label: "目標原価率（F）", value: s.targetCostRate === null ? "未設定" : pct(s.targetCostRate) },
    { label: "目標人件費率（L）", value: s.targetLaborCostRate === null ? "未設定" : pct(s.targetLaborCostRate) },
    { label: "目標FL（F＋L）", value: s.targetFlRate === null ? "未設定" : pct(s.targetFlRate) },
    { label: "目標利益比率", value: s.targetProfitRatio === null ? "未設定" : pct(s.targetProfitRatio) },
  ];
}

/** 月次（棚卸調整後）。出せないものは「未確定」 */
export function statementMonthLines(s: DailyStatement): { label: string; value: string }[] {
  const m = s.month;
  const amount = (n: number | null): string => (n === null ? MONTH_PENDING : yen(n));
  // ★平均は割り切れないので円に丸めて見せる（-0 を出さない）
  const average = (n: number | null): string => (n === null ? MONTH_PENDING : yen(Math.round(n) + 0));
  const rate = (r: number | null): string => (r === null ? MONTH_PENDING : `${(r * 100).toFixed(1)}%`);
  return [
    { label: "売上（税抜）", value: amount(m.salesExclTax) },
    { label: "原価", value: amount(m.cost) },
    { label: "原価率", value: rate(m.costRate) },
    { label: "粗利", value: amount(m.grossProfit) },
    { label: "粗利率", value: rate(m.grossProfitRate) },
    { label: "人件費（店舗合計）", value: amount(m.laborCost) },
    { label: "人件費率", value: rate(m.laborCostRate) },
    { label: "FL率", value: rate(m.flRate) },
    { label: "固定費", value: amount(m.fixedCosts) },
    { label: "営業利益", value: amount(m.operatingProfit) },
    { label: "営業利益率", value: rate(m.operatingProfitRate) },
    // ★平均は月の値で1つずつ。割った日数（売上が入力された営業日）を紙に併記する
    { label: `固定費AV（${String(m.averageDays)}日平均）`, value: average(m.fixedCostsAverage) },
    { label: `営業利益平均（${String(m.averageDays)}日平均）`, value: average(m.operatingProfitAverage) },
  ];
}

/** 人件費が出ない月に、固定費・営業利益が空欄になる訳と、いつ出るか（利用者向け） */
export const STATEMENT_LABOR_NOTE =
  "人件費が計算できない月は、固定費と営業利益（当日・累計）が空欄になります。旧日報から取り込んだ過去の月は、人件費の取込が済むと出ます。今月以降は勤務実績から出ます。";

/** 注記。★出せない理由・除いた金額を、帳票の上で言う（`docs/02` §7.1 と同じ事柄） */
export function statementNotes(s: DailyStatement): string[] {
  const n = s.notes;
  const out = [
    "日別・累計の粗利と営業利益は、仕入ベースの概算です（棚卸調整は月次だけに当てています）。",
    "日別の原価率・累計の FL は、仕入ベースの概算です（月次の原価率は棚卸調整後で、値が異なります）。FL は累計だけです。",
    "達成率・累計差額は、売上が入力された日までの目標の合計（目標累計）と比べたものです。",
    "利益達成は、営業利益（概算）の累計を、売上が入力された営業日までの目標利益の合計で割ったものです。利益目標が 0 以下の月は空欄です。",
  ];
  if (n.missingDates.length > 0) {
    out.push(
      n.calendarUnavailableReason === null
        ? `売上が未入力の日は空欄で、累計に入っていません：${n.missingDates.map(md).join("・")}`
        : `売上の入力が無い ${String(n.missingDates.length)}日ぶんは空欄で、累計に入っていません（営業日カレンダーが未整備のため、定休日も含みます）`,
    );
  }
  if (n.calendarUnavailableReason !== null) {
    out.push("営業日カレンダーが未整備のため、営業日数と日別の客数目標は空欄です。");
  }
  if (n.fixedCostReasons.length > 0) {
    out.push(`固定費・営業利益・FL率は計算できません：${n.fixedCostReasons.join("／")}`);
    // ★理由の文字列では見分けない。月の人件費が出ていないこと自体で見る（理由が固定費の科目だけなら出さない）
    if (s.month.laborCost === null) out.push(STATEMENT_LABOR_NOTE);
  }
  if (s.month.cost === null || s.month.salesExclTax === null) out.push(MONTH_PENDING_NOTE);
  if (n.hasImportedPurchases) out.push(IMPORTED_PURCHASES_WARNING);
  if (n.excludedOtherPurchases > 0) out.push(`区分「その他」の仕入 ${yen(n.excludedOtherPurchases)} は原価に入れていません`);
  return out;
}
